import { useEffect, useMemo, useState, useRef } from 'react'
import { CloudUpload, FileText, Inbox, Upload, X } from 'lucide-react'
import { Button, Card, ConfirmDialog, Input, Select, Toggle, DateInput } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import { EmptyState } from '@/components/common/EmptyState'
import { cn } from '@/lib/cn'
import { USERS_API_MAX_LIMIT } from '@/lib/constants'
import { userSubline } from '@/lib/formatters'
import type { DocumentCategory } from '@/api/services/documents.service'
import {
  useUserDocuments,
  useStaffDocumentCategories,
  useUploadDocuments,
  useDeleteDocument,
  useUpdateDocumentExpiry,
} from '@/features/users/hooks/useUserDocuments'
import {
  useCompanyDocuments,
  useCompanyDocumentCategories,
  useUploadCompanyDocuments,
  useArchiveDocument,
  useUnarchiveDocument,
  useDeleteCompanyDocument,
  useUpdateCompanyDocumentExpiry,
} from '../hooks/useDocuments'
import type { DocumentGroup } from '../utils/documentUtils'
import { EditExpiryModal } from './EditExpiryModal'
import {
  DOCUMENT_TYPE_OPTIONS,
  NO_DOCUMENT_TYPE,
  documentTypeNeedsExpiry,
  missingExpiryMessage,
} from '../utils/documentTypes'
import {
  DOCUMENT_UPLOAD_ACCEPT,
  formatFileSize,
  getDocumentUploadErrorMessage,
  getDocumentUploadHelpText,
  getDocumentCategoryOptions,
  validateDocumentFiles,
} from '../utils/documentUtils'
import { ArchiveDocumentModal } from './ArchiveDocumentModal'
import { GroupedDocumentList } from './GroupedDocumentList'
import { useUsersList } from '@/features/users/hooks/useUsers'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const CATEGORY_FILTER_OPTIONS = getDocumentCategoryOptions(true, 'staff')
const UPLOAD_CATEGORY_OPTIONS = getDocumentCategoryOptions(false, 'staff')
const ADD_CUSTOM_CATEGORY = '__add_custom_category__'

interface StaffDocumentsTabProps {
  /** The authenticated user's id (always available). */
  meId: string
  /** Company context for archive/company-route calls. Null when no company is selected. */
  companyId: string | null
  /** Whether the user can view their own staff documents. */
  canReadOwn: boolean
  /** Whether the user can view other employees' documents. */
  canReadOthers: boolean
  /** Whether the user can upload documents. */
  canCreate: boolean
  /** Whether the user can archive or restore documents. */
  canUpdate: boolean
  /** Whether the user can delete documents. */
  canDelete: boolean
  /** Whether the user can manage (upload/archive/delete) other employees' documents. */
  canManageOthers: boolean
}

export function StaffDocumentsTab({
  meId,
  companyId,
  canReadOwn,
  canReadOthers,
  canCreate,
  canUpdate,
  canDelete: canDeletePermission,
  canManageOthers,
}: StaffDocumentsTabProps): React.ReactElement {
  const [selectedUserId, setSelectedUserId] = useState<string>(meId)
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>('onboarding-documents')
  const [showCustomCategory, setShowCustomCategory] = useState(false)
  const [uploadExpiryDate, setUploadExpiryDate] = useState<string>('')
  const [uploadType, setUploadType] = useState<string>(NO_DOCUMENT_TYPE)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [archiveDocId, setArchiveDocId] = useState<string | null>(null)
  const [archiveDocName, setArchiveDocName] = useState<string | undefined>(undefined)
  const [expiryGroup, setExpiryGroup] = useState<DocumentGroup | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isSelf = selectedUserId === meId
  const searchAcrossStaff = !!companyId && canReadOthers && debouncedSearch.length > 0
  const canReadSelected = searchAcrossStaff || (isSelf ? canReadOwn : canReadOthers)
  const canUpload = canCreate && (isSelf || canManageOthers)
  const canDelete = canDeletePermission && (isSelf || canManageOthers)
  const canArchive = !!companyId && canUpdate && (isSelf || canManageOthers)
  // Expiry editing exists on both the legacy (self) and company routes, so no companyId gate.
  const canEditExpiry = canUpdate && (isSelf || canManageOthers)

  const category = (categoryFilter || undefined) as DocumentCategory | undefined

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(id)
  }, [search])

  // Users list for the employee selector (only loaded when user has read_staff_others)
  const { data: usersData } = useUsersList(
    { limit: USERS_API_MAX_LIMIT, ...(companyId ? { companyId } : {}) },
    { enabled: canReadOthers },
  )
  const userOptions: SelectOption[] = (usersData?.data ?? []).map((u) => {
    const fullName = `${u.firstName} ${u.lastName}`.trim()
    return {
      value: u._id,
      label: fullName || u.email,
      description: userSubline(u.employeeId, u.email),
    }
  })

  // Legacy route is only used for the all-companies view. Company route keeps the chip filter exact.
  const useLegacy = !companyId
  const legacyQuery = useUserDocuments(
    useLegacy ? selectedUserId : null,
    {
      ...(category ? { category } : {}),
      ...(companyId ? { companyId } : {}),
      includeArchived,
    },
    { enabled: canReadSelected },
  )
  const companyQuery = useCompanyDocuments(
    !useLegacy ? companyId : null,
    {
      ownerType: 'staff',
      userId: searchAcrossStaff ? undefined : selectedUserId,
      category,
      search: debouncedSearch || undefined,
      includeArchived,
      enabled: !useLegacy && canReadSelected,
    },
  )

  const { data: documents = [], isLoading, error, refetch } = useLegacy ? legacyQuery : companyQuery
  const legacyCategories = useStaffDocumentCategories(useLegacy && canReadSelected)
  const companyCategories = useCompanyDocumentCategories(
    !useLegacy ? companyId : null,
    'staff',
    !useLegacy && canReadSelected,
  )
  const storedCategories = useLegacy
    ? (legacyCategories.data ?? [])
    : (companyCategories.data ?? [])

  const uploadCategoryOptions = useMemo(() => {
    const known = new Set(UPLOAD_CATEGORY_OPTIONS.map((option) => option.value))
    const custom = storedCategories
      .filter((value) => value && !known.has(value))
      .filter((value, index, values) => values.indexOf(value) === index)
      .sort()
      .map((value) => ({ value, label: value }))
    return [
      ...UPLOAD_CATEGORY_OPTIONS,
      ...custom,
      { value: ADD_CUSTOM_CATEGORY, label: '+ Add custom category…' },
    ]
  }, [storedCategories])
  const categoryFilterOptions = useMemo(
    () => [
      CATEGORY_FILTER_OPTIONS[0],
      ...uploadCategoryOptions.filter((option) => option.value !== ADD_CUSTOM_CATEGORY),
    ],
    [uploadCategoryOptions],
  )

  // Mutations: prefer company scoped when available (supports archive)
  const legacyUpload = useUploadDocuments(isSelf ? meId : null)
  const companyUpload = useUploadCompanyDocuments(companyId)
  const legacyDelete = useDeleteDocument(selectedUserId)
  const companyDelete = useDeleteCompanyDocument(companyId)
  const archiveMutation = useArchiveDocument(companyId)
  const unarchiveMutation = useUnarchiveDocument(companyId)
  const legacyExpiry = useUpdateDocumentExpiry(selectedUserId)
  const companyExpiry = useUpdateCompanyDocumentExpiry(companyId)
  const expiryMutation = useLegacy ? legacyExpiry : companyExpiry

  const processFiles = (files: FileList | File[]): void => {
    const list = Array.from(files)
    const err = validateDocumentFiles(list)
    if (err) {
      setUploadError(err)
      setSelectedFiles([])
      return
    }
    setUploadError(null)
    setSelectedFiles((prev) => [...prev, ...list])
  }

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (!canUpload || !e.dataTransfer.files?.length) return
    processFiles(e.dataTransfer.files)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!canUpload) return
    if (!e.target.files?.length) return
    processFiles(e.target.files)
    e.target.value = ''
  }

  const removeFile = (index: number): void => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
    setUploadError(null)
  }

  const handleUploadSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!canUpload) return
    setUploadError(null)
    if (selectedFiles.length === 0) {
      setUploadError('Select at least one file')
      return
    }
    const categoryToUpload = uploadCategory.trim()
    if (!categoryToUpload) {
      setUploadError('Enter or select a category')
      return
    }
    const expiryMissing = missingExpiryMessage(uploadType, uploadExpiryDate)
    if (expiryMissing) {
      setUploadError(expiryMissing)
      return
    }
    const expiryDate = uploadExpiryDate
      ? new Date(`${uploadExpiryDate}T23:59:59.999Z`).toISOString()
      : undefined
    const documentType = uploadType || undefined
    const resetForm = (): void => {
      setSelectedFiles([])
      setUploadExpiryDate('')
      setUploadType(NO_DOCUMENT_TYPE)
      setUploadCategory('onboarding-documents')
      setShowCustomCategory(false)
      setUploadError(null)
      refetch()
    }
    if (companyId) {
      companyUpload.mutate(
        {
          files: selectedFiles,
          params: {
            ownerType: 'staff',
            category: categoryToUpload,
            userId: selectedUserId,
            ...(expiryDate ? { expiryDate } : {}),
            ...(documentType ? { documentType } : {}),
          },
        },
        {
          onSuccess: resetForm,
          onError: (err) => setUploadError(getDocumentUploadErrorMessage(err)),
        },
      )
    } else {
      legacyUpload.mutate(
        { files: selectedFiles, category: categoryToUpload, expiryDate, documentType },
        {
          onSuccess: resetForm,
          onError: (err) => setUploadError(getDocumentUploadErrorMessage(err)),
        },
      )
    }
  }

  const handleExpiryConfirm = (expiryDate: string | null, documentType?: string | null): void => {
    if (!canEditExpiry || !expiryGroup) return
    expiryMutation.mutate(
      { id: expiryGroup.representativeDocument.id, expiryDate, documentType },
      { onSuccess: () => setExpiryGroup(null) },
    )
  }

  const handleArchiveConfirm = (archiveReason?: string): void => {
    if (!canArchive) return
    if (!archiveDocId) return
    archiveMutation.mutate(
      { id: archiveDocId, archiveReason },
      { onSuccess: () => setArchiveDocId(null) },
    )
  }

  const handleConfirmDelete = (): void => {
    if (!canDelete) return
    if (!deleteId) return
    if (companyId) {
      companyDelete.mutate(deleteId, { onSuccess: () => setDeleteId(null) })
    } else {
      legacyDelete.mutate(deleteId, { onSuccess: () => setDeleteId(null) })
    }
  }

  const isPendingUpload = companyUpload.isPending || legacyUpload.isPending
  const isPendingDelete = companyDelete.isPending || legacyDelete.isPending

  return (
    <div className="space-y-5">
      {canUpload && (
        <form
          onSubmit={handleUploadSubmit}
          className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={DOCUMENT_UPLOAD_ACCEPT}
            multiple
            onChange={handleFileSelect}
            className="sr-only"
            aria-label="Choose files"
          />
          <div className="mb-3 grid gap-3 sm:grid-cols-2 sm:items-end xl:grid-cols-4">
            {canManageOthers && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Employee
                </label>
                <Select
                  options={userOptions}
                  value={selectedUserId}
                  onChange={(v) => setSelectedUserId(v || meId)}
                  placeholder="Select employee…"
                  searchable
                  searchPlaceholder="Search name, ID or email…"
                  className="h-11 w-full rounded-xl"
                />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="uploadDocumentType"
                className="text-xs font-medium text-gray-600 dark:text-gray-300"
              >
                Type
              </label>
              <Select
                id="uploadDocumentType"
                options={DOCUMENT_TYPE_OPTIONS}
                value={uploadType}
                onChange={(value) => {
                  setUploadType(value)
                  setUploadError(null)
                }}
                className="h-11 w-full rounded-xl"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                Category
              </label>
              <Select
                options={uploadCategoryOptions}
                value={uploadCategory}
                onChange={(value) => {
                  if (value === ADD_CUSTOM_CATEGORY) {
                    setUploadCategory('')
                    setShowCustomCategory(true)
                    return
                  }
                  setUploadCategory(value)
                  setShowCustomCategory(false)
                }}
                placeholder="Category"
                className="h-11 w-full rounded-xl"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="uploadExpiryDate"
                className="text-xs font-medium text-gray-600 dark:text-gray-300"
              >
                Expiry date{' '}
                <span className="text-gray-400">
                  {documentTypeNeedsExpiry(uploadType) ? '(required)' : '(optional)'}
                </span>
              </label>
              <div className="flex items-center gap-2">
                <DateInput
                  id="uploadExpiryDate"
                  value={uploadExpiryDate}
                  onChange={(e) => {
                    setUploadExpiryDate(e.target.value)
                    setUploadError(null)
                  }}
                  className="h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                />
                {uploadExpiryDate && (
                  <button
                    type="button"
                    className="text-xs text-gray-500 underline hover:text-danger dark:text-gray-400"
                    onClick={() => setUploadExpiryDate('')}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {showCustomCategory && (
            <div className="mb-3 max-w-sm">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                Custom category
              </label>
              <Input
                value={uploadCategory}
                onChange={(event) => setUploadCategory(event.target.value)}
                placeholder="Enter a category name"
                maxLength={80}
                autoFocus
              />
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem] md:items-stretch">
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
              }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
              onDragLeave={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false)
              }}
              onDrop={handleDrop}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  fileInputRef.current?.click()
                }
              }}
              aria-label="Drop files or click to browse"
              className={cn(
                'flex h-11 min-h-11 flex-col items-center justify-center gap-0.5 rounded-xl border border-dashed px-4 text-center transition-colors cursor-pointer',
                isDragging
                  ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                  : 'border-gray-300 bg-gray-50/60 text-gray-600 hover:border-primary/50 hover:bg-primary/5 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-300',
              )}
            >
              <div className="flex items-center gap-2">
                <CloudUpload className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                  {isDragging
                    ? 'Drop to attach'
                    : selectedFiles.length > 0
                      ? `${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'} selected · click to add more`
                      : 'Drag & drop files, or click to browse'}
                </span>
              </div>
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={selectedFiles.length === 0 || !uploadCategory.trim()}
              loading={isPendingUpload}
              className="h-11 min-h-11 w-full rounded-xl px-5 text-sm"
            >
              <Upload className="h-4 w-4" aria-hidden />
              <span>
                {selectedFiles.length > 0
                  ? `Upload ${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'}`
                  : 'Upload'}
              </span>
            </Button>
          </div>

          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {getDocumentUploadHelpText()}
          </p>

          {uploadError && (
            <p className="mt-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-1.5 text-sm text-danger" role="alert">
              {uploadError}
            </p>
          )}

          {selectedFiles.length > 0 && (
            <ul className="mt-3 space-y-1.5" role="list">
              {selectedFiles.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/50 py-1.5 pl-2 pr-1.5 dark:border-gray-700 dark:bg-gray-900/40"
                >
                  <FileText className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                  <span
                    className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100"
                    title={file.name}
                  >
                    {file.name}
                  </span>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    className="shrink-0 p-1 text-gray-500 hover:text-danger dark:text-gray-400 dark:hover:text-danger"
                    onClick={() => removeFile(index)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </form>
      )}

      <Card
        className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
        contentClassName="space-y-4 p-4 md:p-5"
      >
        <div className="flex flex-col gap-3 border-b border-gray-100 pb-3 dark:border-gray-700 md:flex-row md:items-center md:justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {isSelf ? 'Your documents' : 'Employee documents'}
          </h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            {canReadOthers && (
              <Select
                options={userOptions}
                value={selectedUserId}
                onChange={(v) => setSelectedUserId(v || meId)}
                placeholder="Select employee…"
                searchable
                searchPlaceholder="Search name, ID or email…"
                className="w-full sm:w-72"
              />
            )}
            {canReadOthers && (
              <div className="w-full sm:w-72">
                <Input
                  label="Search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Name or email"
                />
              </div>
            )}
            {canReadSelected && (
              <Select
                options={categoryFilterOptions}
                value={categoryFilter}
                onChange={setCategoryFilter}
                placeholder="All categories"
                className="w-full sm:w-44"
              />
            )}
            {canReadSelected && canArchive && (
              <Toggle
                checked={includeArchived}
                onChange={setIncludeArchived}
                label="Show archived"
              />
            )}
          </div>
        </div>

        {!canReadSelected ? (
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            You do not have permission to view these staff documents.
          </div>
        ) : isLoading ? (
          <SectionLoader variant="list" label="Loading staff documents" />
        ) : error ? (
          <QueryErrorState
            error={error}
            size="inline"
            title="Couldn't load documents"
            onRetry={refetch}
          />
        ) : documents.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No documents yet"
            description={canUpload ? 'Upload your first file using the panel above.' : 'Once documents are added they will appear here.'}
          />
        ) : (
          <GroupedDocumentList
            documents={documents}
            canArchive={canArchive}
            canDelete={canDelete}
            canEditExpiry={canEditExpiry}
            isArchivePending={archiveMutation.isPending}
            isUnarchivePending={unarchiveMutation.isPending}
            isDeletePending={isPendingDelete}
            onArchive={(document) => {
              setArchiveDocId(document.id)
              setArchiveDocName(document.originalName)
            }}
            onUnarchive={(documentId) => unarchiveMutation.mutate(documentId)}
            onDelete={setDeleteId}
            onEditExpiry={setExpiryGroup}
            expiryActionLabel="Edit type and expiry"
          />
        )}
      </Card>

      <ArchiveDocumentModal
        isOpen={archiveDocId !== null}
        onClose={() => setArchiveDocId(null)}
        onConfirm={handleArchiveConfirm}
        isPending={archiveMutation.isPending}
        documentName={archiveDocName}
      />

      <EditExpiryModal
        isOpen={expiryGroup !== null}
        onClose={() => setExpiryGroup(null)}
        onConfirm={handleExpiryConfirm}
        isPending={expiryMutation.isPending}
        documentName={expiryGroup?.title}
        currentExpiry={expiryGroup?.expiryDate ?? null}
        editType
        currentType={expiryGroup?.representativeDocument.documentType ?? null}
      />

      <ConfirmDialog
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete document"
        message="Permanently delete this document? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={isPendingDelete}
      />
    </div>
  )
}

StaffDocumentsTab.displayName = 'StaffDocumentsTab'
